import DOMPurify from 'dompurify';
import { BRAND_SECONDARY_MAIN } from '../../theme/managementTheme';

// Personalization variables of marketing mails that can be inserted into text,
// button labels/links and the subject line. Fallbacks are configured per mail in
// variableFallbacks. Transactional templates offer the placeholders their own
// content already uses instead (see collectVariableTokens).
export const MARKETING_VARIABLES = [
  { token: '{{vorname}}', label: 'Vorname' },
  { token: '{{standort}}', label: 'Standort' },
  { token: '{{buchungslink}}', label: 'Buchungslink' },
  { token: '{{abmeldelink}}', label: 'Abmeldelink' },
];

// E-mail-safe font stacks for the message style settings.
export const FONT_FAMILIES = [
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial / Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: "'Times New Roman', Times, serif", label: 'Times New Roman' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
  { value: "'Courier New', Courier, monospace", label: 'Courier New' },
];

// German labels for the element types used across palette, canvas and panels.
export const ELEMENT_TYPE_LABELS = {
  text: 'Text',
  image: 'Bild',
  button: 'Button',
  spacer: 'Leerfeld / Abstand',
  divider: 'Trennlinie',
  html: 'HTML',
};

// Order of the tiles in the "Grundelemente" tab.
export const PALETTE_ELEMENT_TYPES = ['text', 'image', 'button', 'spacer', 'divider', 'html'];

// Per-element device visibility. Duplicating an element and restricting the two
// copies to different device classes is how a mail is optimized for mobile.
export const ELEMENT_VISIBILITY_OPTIONS = [
  { value: 'all', label: 'Alle Geräte' },
  { value: 'mobile', label: 'Nur Mobilgeräte' },
  { value: 'desktop', label: 'Nur Desktop-Geräte' },
];

export const normalizeVisibility = value =>
  ELEMENT_VISIBILITY_OPTIONS.some(option => option.value === value) ? value : 'all';

// German label + chip color per effectiveStatus of a marketing mail.
export const STATUS_META = {
  draft: { label: 'Entwurf', color: 'default' },
  saved: { label: 'Gespeichert', color: 'success' },
  inUse: { label: 'Wird im Flow verwendet', color: 'warning' },
};

// Upload constraints for editor image assets. The backend caps assets at 1 MB
// (base64 payloads must stay below the global JSON body limit).
export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
export const MAX_ASSET_BYTES = 1024 * 1024;

export const newId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const deepClone = value => JSON.parse(JSON.stringify(value));

// Sanitizes user-authored HTML before it is stored or injected into the canvas.
export const sanitizeHtml = html =>
  DOMPurify.sanitize(String(html ?? ''), {
    ALLOWED_TAGS: ['a', 'b', 'strong', 'i', 'em', 'u', 'p', 'br', 'span', 'div', 'ul', 'ol', 'li', 'h1', 'h2', 'h3'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
  });

// Sanitizes an HTML element's markup for the canvas preview. Mail HTML is
// table-based and often carries its own <style>, so the allow-list is wider
// than for text elements — the server sanitizes again before sending.
export const sanitizeEmbeddedHtml = html =>
  DOMPurify.sanitize(String(html ?? ''), {
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'base', 'link', 'meta'],
    FORBID_ATTR: ['srcdoc'],
    WHOLE_DOCUMENT: false,
  });

// True for markup that carries its own <html>/<body> wrapper.
export const isFullHtmlDocument = html => /<(html|body)[\s>]/i.test(String(html ?? ''));

// Reduces a full HTML document to the markup that can live inside another
// document: its <style> blocks plus the body content. Fragments pass through.
export const extractHtmlFragment = html => {
  const source = String(html ?? '');
  if (!isFullHtmlDocument(source)) return source;
  const styles = (source.match(/<style[\s\S]*?<\/style>/gi) || []).join('');
  const bodyMatch = source.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const body = bodyMatch ? bodyMatch[1] : source.replace(/<[/]?(?:html|head|body)[^>]*>/gi, '');
  return `${styles}${body}`;
};

// Collects the {{placeholder}} tokens used in a piece of content. Drives the
// variable picker for transactional templates, whose available variables depend
// on the mail the template is used for.
export const collectVariableTokens = (...contents) => {
  const tokens = new Set();
  contents.forEach(content => {
    const matches = String(content ?? '').match(/\{\{\s*[\w.]+\s*(\|[^}]*)?\}\}/g) || [];
    matches.forEach(match => tokens.add(match.replace(/\s+/g, '')));
  });
  return [...tokens].sort();
};

// Creates a new element of the given type with sensible German defaults.
export const createDefaultElement = type => {
  const base = { elementId: newId(), type };
  switch (type) {
    case 'text':
      return { ...base, html: 'Neuer Text', align: 'left', padding: 12 };
    case 'image':
      return { ...base, src: '', alt: '', widthPercent: 100, align: 'center', link: '', padding: 12 };
    case 'button':
      return {
        ...base,
        label: 'Geburtstag planen',
        link: '',
        backgroundColor: BRAND_SECONDARY_MAIN,
        textColor: '#ffffff',
        align: 'center',
        borderRadius: 6,
        padding: 12,
        innerPadding: 12,
        fontSize: 16,
      };
    case 'spacer':
      return { ...base, height: 20 };
    case 'divider':
      return { ...base, color: '#dddddd', thickness: 1, widthPercent: 100, paddingTop: 12, paddingBottom: 12 };
    case 'html':
      return { ...base, html: '', padding: 0 };
    default:
      return base;
  }
};

// Deep copy of a saved/duplicated element with a fresh elementId.
export const cloneElementWithNewId = element => ({ ...deepClone(element), elementId: newId() });

// Turns a document that was authored in the former separate HTML mode into a
// regular Baukasten document with one HTML element, so there is only one editor
// and raw HTML is just another building block.
const migrateHtmlModeToElement = doc => {
  if (doc.contentMode !== 'html') return;
  const html = typeof doc.htmlContent === 'string' ? doc.htmlContent : '';
  doc.contentMode = 'blocks';
  if (!html.trim()) return;
  doc.sections = [
    { sectionId: newId(), elements: [{ elementId: newId(), type: 'html', html, padding: 0 }] },
  ];
};

// Fills in all optional parts of a server document so the editor can rely on
// them being present.
export const normalizeDoc = raw => {
  const doc = deepClone(raw ?? {});
  doc.contentMode = doc.contentMode === 'html' ? 'html' : 'blocks';
  doc.htmlContent = typeof doc.htmlContent === 'string' ? doc.htmlContent : '';
  doc.messageStyle = {
    width: 600,
    backgroundColor: '#f4f4f4',
    contentBackgroundColor: '#ffffff',
    fontFamily: FONT_FAMILIES[0].value,
    fontSize: 16,
    textColor: '#333333',
    ...(doc.messageStyle || {}),
  };
  doc.sections = Array.isArray(doc.sections) ? doc.sections : [];
  doc.sections = doc.sections.map(section => ({
    sectionId: section.sectionId || newId(),
    elements: Array.isArray(section.elements) ? section.elements : [],
  }));
  doc.footer = {
    text: '',
    unsubscribeLabel: 'Abmelden',
    ...(doc.footer || {}),
  };
  doc.variableFallbacks = {
    vorname: '',
    standort: '',
    buchungslink: '',
    ...(doc.variableFallbacks || {}),
  };
  migrateHtmlModeToElement(doc);
  return doc;
};

/* --------------------- transactional mail templates --------------------- */

// Brings a MailTemplate into the same editor shape the Baukasten works with.
// Templates that were never opened in the editor carry hand-written HTML only,
// which becomes a single HTML element.
export const normalizeTemplateDoc = raw => {
  const template = deepClone(raw ?? {});
  const usesBlocks = template.contentMode === 'blocks' && Array.isArray(template.sections);
  const doc = {
    _id: template._id,
    key: template.key || '',
    description: template.description || '',
    subject: template.subject || '',
    textContent: typeof template.textContent === 'string' ? template.textContent : '',
    contentMode: 'blocks',
    messageStyle: {
      width: 600,
      contentBackgroundColor: '#ffffff',
      fontFamily: FONT_FAMILIES[0].value,
      fontSize: 16,
      textColor: '#333333',
      ...(template.messageStyle || {}),
    },
    sections: [],
    // Kept so the marketing-shaped editor components can render unconditionally.
    footer: { text: '', unsubscribeLabel: '' },
    variableFallbacks: {},
  };
  if (usesBlocks) {
    doc.sections = template.sections.map(section => ({
      sectionId: section.sectionId || newId(),
      elements: Array.isArray(section.elements) ? section.elements : [],
    }));
    return doc;
  }
  const html = typeof template.htmlContent === 'string' ? template.htmlContent : '';
  doc.sections = [
    {
      sectionId: newId(),
      elements: html.trim() ? [{ elementId: newId(), type: 'html', html, padding: 0 }] : [],
    },
  ];
  return doc;
};

// The fields PATCHed to /mailTemplate. htmlContent is compiled from the blocks
// server-side, so it is deliberately not sent.
export const buildTemplateUpdatePayload = doc => ({
  subject: doc.subject,
  contentMode: 'blocks',
  sections: doc.sections,
  messageStyle: doc.messageStyle,
  textContent: doc.textContent,
});

// The editable fields sent to updateEmail. The server sets status to 'saved'.
export const buildUpdatePayload = doc => ({
  name: doc.name,
  senderName: doc.senderName,
  replyToAddress: doc.replyToAddress,
  subject: doc.subject,
  preheader: doc.preheader,
  contentMode: 'blocks',
  htmlContent: '',
  messageStyle: doc.messageStyle,
  sections: doc.sections,
  footer: doc.footer,
  variableFallbacks: doc.variableFallbacks,
  bookingLink: doc.bookingLink,
  isTemplate: doc.isTemplate,
});
